import React, { useState, useEffect } from 'react';
import { Form, Card, Space, Switch, Input, Avatar, message } from 'antd';
import FormItem from '@/common/FormItem';
import styles from '@/common/Layout/index.module.less';
import CustomTable from 'common/Content/CustomTable';
import CustomButton from '@/common/CustomButton';
import { history } from 'app/history';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAllUsers,
  deleteUser,
  getAllDesignations,
  fetchUserMatrix
} from '../../redux/slice';
import { EditOutlined } from '@ant-design/icons';
import CustomModal from '@/common/CustomModal';
import PasswordConfirmModal from '@/common/PasswordConfirmModal';
import { roles } from 'utilities/helpers';

const AdminPage = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const {
    allUsers,
    pagination,
    allDisignations,
    userMatrix,
    getUserMatrixLoading
  } = useSelector((state) => state.admin);

  const { Search } = Input;

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('');
  const [level, setLevel] = useState('');
  const [isModal, setIsModal] = useState(false);
  const [pwdModalVisible, setPwdModalVisible] = useState(false);
  const [pwdModalLoading, setPwdModalLoading] = useState(false);
  const [pwdModalError, setPwdModalError] = useState('');
  const [pendingToggle, setPendingToggle] = useState(null);

  const handleModel = (role, level) => {
    setRole(role);
    setLevel(level);
    setIsModal(true);
    // Fetch user matrix data when modal opens
    if (role && level) {
      const payload = { role, level };
      console.log('Fetching user matrix for modal with payload:', payload);
      dispatch(fetchUserMatrix(payload));
    }
  };

  console.log(level, role, isModal);
  const handleChange = (e) => {
    setSearch(e.target.value);
  };

  useEffect(() => {
    handleGetAllUsers();
    handleGetAllDesignations();
  }, [page, search]);

  const handleGetAllDesignations = () => {
    const payload = {};
    dispatch(getAllDesignations(payload));
  };

  const designation = allDisignations;
  console.log(designation);

  // Helper to render permission avatars based on code from API (e.g. 'VC', 'VCE')
  const renderPermission = (code) => {
    if (!code) return 'NA';

    return (
      <Space>
        {code.includes('V') && (
          <Avatar
            style={{ backgroundColor: 'blue', verticalAlign: 'middle' }}
            size="small"
          >
            V
          </Avatar>
        )}
        {code.includes('C') && (
          <Avatar
            style={{ backgroundColor: 'Green', verticalAlign: 'middle' }}
            size="small"
          >
            C
          </Avatar>
        )}
        {code.includes('E') && (
          <Avatar
            style={{ backgroundColor: '#FFBF00', verticalAlign: 'middle' }}
            size="small"
          >
            E
          </Avatar>
        )}
        {code.includes('D') && (
          <Avatar
            style={{ backgroundColor: '#dc3545', verticalAlign: 'middle' }}
            size="small"
          >
            D
          </Avatar>
        )}
      </Space>
    );
  };

  // Build transactions array from API response (userMatrix)
  // API returns a single object, but Redux stores it as an array, so get the first element
  const matrix =
    userMatrix && userMatrix.length > 0 ? userMatrix[0] : userMatrix || {};
  const transactions = [
    {
      name: 'Material Receipt',
      permission: renderPermission(matrix.material_receipt)
    },
    {
      name: 'Material Movement',
      permission: renderPermission(matrix.material_movement)
    },
    {
      name: 'Sampling / TRF',
      permission: renderPermission(matrix.sampling_trf)
    },
    {
      name: 'Material Status Update',
      permission: renderPermission(matrix.material_status_update)
    },
    {
      name: 'Dispensing Request',
      permission: renderPermission(matrix.dispensing_request)
    },
    {
      name: 'Dispensing Execution',
      permission: renderPermission(matrix.dispensing_execution)
    },
    {
      name: 'Dispensing Approval',
      permission: renderPermission(matrix.dispensing_approval)
    },
    {
      name: 'Stock Hold / Release',
      permission: renderPermission(matrix.stock_hold_release)
    },
    {
      name: 'Stock Deduction',
      permission: renderPermission(matrix.stock_deduction)
    },
    {
      name: 'Stock Reconciliation',
      permission: renderPermission(matrix.stock_reconciliation)
    },
    {
      name: 'Material Return Request',
      permission: renderPermission(matrix.material_return_request)
    },
    {
      name: 'Code to Code Transfer',
      permission: renderPermission(matrix.code_to_code_transfer)
    },
    {
      name: 'Vendor / Material Master',
      permission: renderPermission(matrix.vendor_material_master)
    },
    {
      name: 'Line Clearance',
      permission: renderPermission(matrix.line_clearance)
    },
    {
      name: 'Material Verification',
      permission: renderPermission(matrix.material_verification)
    },
    {
      name: 'Material Verification Report',
      permission: renderPermission(matrix.material_verification_report)
    },
    {
      name: 'Product Verification',
      permission: renderPermission(matrix.product_verification)
    },
    {
      name: 'Product Verification Report',
      permission: renderPermission(matrix.product_verification_report)
    },
    {
      name: 'DRUM Label Generation',
      permission: renderPermission(matrix.drum_label_generation)
    },
    {
      name: 'DRUM Allocation',
      permission: renderPermission(matrix.drum_allocation)
    },
    {
      name: 'Sieve/Screen Label Generation',
      permission: renderPermission(matrix.sieve_label_generation)
    },
    {
      name: 'Sieve/Screen Allocation',
      permission: renderPermission(matrix.sieve_allocation)
    },
    {
      name: 'WIP Weighing Labels',
      permission: renderPermission(matrix.wip_weighing_labels)
    },
    {
      name: 'BOM Creation',
      permission: renderPermission(matrix.bom_creation)
    },
    {
      name: 'Equipment Master',
      permission: renderPermission(matrix.equipment_master)
    },
    {
      name: 'EBMR Configuration',
      permission: renderPermission(matrix.ebmr_configuration)
    },
    {
      name: 'EBMR Execution',
      permission: renderPermission(matrix.ebmr_execution)
    },
    {
      name: 'EBMR Event Review',
      permission: renderPermission(matrix.ebmr_event_review)
    },
    {
      name: 'EBMR Approval',
      permission: renderPermission(matrix.ebmr_approval)
    },
    {
      name: 'Electronic Logbooks',
      permission: renderPermission(matrix.electronic_logbooks)
    },
    {
      name: 'Reports',
      permission: renderPermission(matrix.reports)
    },
    {
      name: 'Audit Trail',
      permission: renderPermission(matrix.audit_trail)
    },
    {
      name: 'User Management',
      permission: renderPermission(matrix.user_management)
    }
  ];

  const handleGetAllUsers = () => {
    const payload = { page, search };
    dispatch(getAllUsers(payload));
  };

  const openPwdModalForToggle = (record) => {
    setPendingToggle({ id: record.id, is_active: !record?.is_active });
    setPwdModalError('');
    setPwdModalVisible(true);
  };

  const handlePwdConfirm = (password) => {
    if (!pendingToggle) return;
    setPwdModalLoading(true);
    setPwdModalError('');
    dispatch(deleteUser({ ...pendingToggle, password })).then((response) => {
      const payload = response?.payload || {};
      if (payload.success) {
        setPwdModalLoading(false);
        setPwdModalVisible(false);
        setPendingToggle(null);
        message.success(payload.message || 'User updated successfully');
        handleGetAllUsers();
      } else {
        setPwdModalLoading(false);
        setPwdModalError(
          payload.message || 'Unable to save changes. Please try again.'
        );
      }
    });
  };

  const handlePwdCancel = () => {
    if (pwdModalLoading) return;
    setPwdModalVisible(false);
    setPwdModalError('');
    setPendingToggle(null);
  };

  const handleClose = () => {
    setIsModal(false);
  };

  const column1 = [
    {
      title: 'Transactions',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      width: '100px',
      render: (text) => {
        return <b style={{ fontSize: '12px' }}>{text}</b>;
      }
    },
    {
      title: `Permissions`,
      dataIndex: 'permission',
      key: 'permission',
      ellipsis: true,
      width: '100px'
    }
  ];
  const columns = [
    {
      title: 'Name',
      dataIndex: 'first_name',
      key: 'first_name',
      ellipsis: true,
      width: '100px'
    },
    {
      title: 'Employee ID',
      dataIndex: 'employee_id',
      key: 'employee_id',
      ellipsis: true,
      width: '150px'
    },
    {
      title: 'E-mail',
      dataIndex: 'email',
      key: 'email'
      // ellipsis: true,
      // width: '100px'
    },
    {
      title: 'Designation',
      dataIndex: 'designation_id',
      key: 'designation_id',
      // ellipsis: true,
      // width: '100px',
      render: (_, record) => {
        // return record?.role?.replace(/[^a-zA-Z0-9 ]/g, ' ').toUpperCase();
        const des = designation.filter(
          (designation) => designation.id == record?.designation_id
        );
        return des[0]?.designation || 'NA';
      }
    },
    {
      title: 'Permissions',
      dataIndex: 'material_code',
      key: 'material_code',
      render: (_, record) => {
        return (
          <p
            onClick={() => handleModel(record?.role, record?.level)}
            style={{ color: '#e80e94', cursor: 'pointer' }}
          >
            View Matrix
          </p>
        );
        // if (record.level == 1) {
        //   return 'View';
        // } else if (record.level == 2) {
        //   return 'View,Create';
        // } else if (record.level == 3) {
        //   return 'View, Create, Edit';
        // }
      }
    },
    {
      title: 'Action',
      dataIndex: 'inhouse_batch_number',
      key: 'inhouse_batch_number',
      ellipsis: true,
      width: '100px',
      render: (_, record) => {
        return (
          <Space>
            <a href={`user-creation?id=${record.id}`}>
              <EditOutlined />
            </a>
            <Switch
              size="small"
              checked={record.is_active}
              disabled={pwdModalLoading && pendingToggle?.id === record.id}
              onChange={() => openPwdModalForToggle(record)}
            />
          </Space>
        );
      }
    }
  ];

  return (
    <>
      <Form form={form} layout="vertical">
        <Card
          title={<span>User Details</span>}
          className={`${styles.cardStyles_approver}`}
          extra={
            <Space>
              <Search
                onChange={(e) => handleChange(e)}
                className={`w-100 ${styles.searchbar}`}
                justify="start"
                align="start"
                placeholder="Search by Emp ID or Name"
              />
              <CustomButton
                className={styles.inwardButton}
                onClick={() => history.push('/user-creation')}
                type="primary"
              >
                Create User
              </CustomButton>
            </Space>
          }
        >
          <div className={`${styles.table_container}`}>
            <CustomTable
              columns={columns}
              dataSource={allUsers}
              pagination={{
                size: 'small',
                total: pagination?.total_records || 0,
                showSizeChanger: false,
                onChange: (page) => {
                  setPage(page);
                }
              }}
            />
          </div>
        </Card>
        {isModal ? (
          <CustomModal
            title="Permission Matrix"
            visible={isModal}
            width="50%"
            footer={null}
            className={styles.customModal}
            closable={false}
          >
            <p style={{ color: '#f536a5' }}>
              Role: <b>{roles(role, level)}</b> , Level: <b>{level}</b>
            </p>
            <div className={`${styles.table_row}`}>
              <CustomTable
                columns={column1}
                dataSource={transactions}
                pagination={false}
                loading={getUserMatrixLoading}
              />
            </div>
            <br />
            <FormItem className="text-center">
              <CustomButton
                className={styles.inwardButton}
                onClick={handleClose}
                type="primary"
                size="small"
              >
                Close
              </CustomButton>
            </FormItem>
          </CustomModal>
        ) : (
          ''
        )}
        <br />
        <FormItem className="text-center">
          <CustomButton
            className={styles.inwardButton}
            onClick={() => history.back()}
            type="primary"
          >
            Back to Home
          </CustomButton>
        </FormItem>
      </Form>
      <PasswordConfirmModal
        visible={pwdModalVisible}
        title={
          pendingToggle?.is_active
            ? 'Confirm activation with your password'
            : 'Confirm deactivation with your password'
        }
        description={
          pendingToggle?.is_active
            ? 'Please re-enter your password to activate this user.'
            : 'Please re-enter your password to deactivate this user.'
        }
        okText="Confirm"
        loading={pwdModalLoading}
        errorMessage={pwdModalError}
        onConfirm={handlePwdConfirm}
        onCancel={handlePwdCancel}
      />
    </>
  );
};
export default AdminPage;
